import type { Move, ResolvedMove, RoundResultView, SkillId, Stance } from '@shared/protocol/messages';
import { RULES } from './rules';
import { nextU32 } from './rng';
import { judge } from './rps';

/**
 * Pure, deterministic combat engine. No rendering, no timers, no randomness
 * except the seeded state carried in BattleState. The Go server implements the
 * same algorithm (backend/internal/combat/engine.go); both are checked against
 * shared/schemas/combat-vectors.json.
 */

export interface CombatStats {
  name: string;
  maxHp: number;
  atk: number;
  def: number;
  luck: number;
  skill: SkillId;
}

export type BattleWinner = 0 | 1 | 'draw' | null;

export interface BattleState {
  readonly fighters: readonly [CombatStats, CombatStats];
  readonly hp: readonly [number, number];
  readonly combo: readonly [number, number];
  /** Number of rounds already resolved. */
  readonly round: number;
  readonly maxRounds: number;
  readonly rng: number;
  readonly winner: BattleWinner;
  readonly log: readonly RoundResultView[];
  /** Rounds each seat must still wait before it may use a stance again (0 = ready). */
  readonly stanceCooldown: readonly [number, number];
  /** Ink per seat (0..RULES.ink.max); a full gauge unlocks the ultimate. */
  readonly ink: readonly [number, number];
  /** Each seat's combo chain: its latest all-different winning moves. */
  readonly chain: readonly [readonly Move[], readonly Move[]];
}

export const STANCES: readonly Stance[] = ['none', 'guard', 'focus', 'ultimate'];

export function isStance(v: unknown): v is Stance {
  return v === 'none' || v === 'guard' || v === 'focus' || v === 'ultimate';
}

export function ultimateReady(ink: number): boolean {
  return ink >= RULES.ink.max;
}

/**
 * The stance that really applies: none when the move timed out, while cooling
 * down (guard/focus) or without a full ink gauge (ultimate).
 */
export function effectiveStance(requested: Stance | undefined, cooldown: number, move: ResolvedMove, ink = 0): Stance {
  if (!requested || move === 'timeout') return 'none';
  if (requested === 'ultimate') return ultimateReady(ink) ? 'ultimate' : 'none';
  return cooldown > 0 ? 'none' : requested;
}

/** Cooldown after a round in which `applied` was used (the ultimate does not start one). */
export function nextStanceCooldown(cooldown: number, applied: Stance): number {
  return applied === 'guard' || applied === 'focus' ? RULES.stances.stanceCooldownRounds : Math.max(0, cooldown - 1);
}

/** Integer percent of a max HP, at least 1. */
function percentOf(maxHp: number, percent: number): number {
  return Math.max(1, Math.floor((maxHp * percent) / 100));
}

/** Adds a winning move to a chain; true when the chain now bursts. */
function extendChain(chain: readonly Move[], move: Move): { chain: Move[]; burst: boolean } {
  const len = RULES.chain.length;
  const next = [...chain, move].slice(-len);
  // Keep only the run of all-different moves at the end.
  const cut = next.length > 1 ? next.lastIndexOf(move, next.length - 2) : -1;
  const run = cut >= 0 ? next.slice(cut + 1) : next;
  return run.length >= len ? { chain: [], burst: true } : { chain: run, burst: false };
}

export function createBattle(
  a: CombatStats,
  b: CombatStats,
  seed: number,
  maxRounds: number = RULES.battle.maxRounds,
): BattleState {
  return {
    fighters: [a, b],
    hp: [a.maxHp, b.maxHp],
    combo: [0, 0],
    round: 0,
    maxRounds,
    rng: seed >>> 0,
    winner: null,
    log: [],
    stanceCooldown: [0, 0],
    ink: [0, 0],
    chain: [[], []],
  };
}

export interface DamageInput {
  attacker: CombatStats;
  defender: CombatStats;
  attackerMove: ResolvedMove;
  defenderMove: ResolvedMove;
  /** Attacker's combo count including this win (>= 1). */
  combo: number;
  critRoll: number;
  dodgeRoll: number;
  /** Attacker's HP before the hit (Marker Rage); defaults to full HP. */
  attackerHp?: number;
  /** Applied stances (default 'none'). */
  attackerStance?: Stance;
  defenderStance?: Stance;
}

export interface DamageOutput {
  damage: number;
  crit: boolean;
  dodged: boolean;
  shielded: boolean;
}

/**
 * Damage = max(1, ATK - DEF * 0.5), then percent bonuses (combo, crit, skills).
 * Everything is computed in integer hundredths so JS and Go agree exactly.
 */
export function computeDamage(inp: DamageInput): DamageOutput {
  const d = RULES.damage;
  const sp = RULES.skillParams;
  const { attacker, defender } = inp;

  const ignoreDef = attacker.skill === 'sharp_edge' && inp.attackerMove === 'scissors';
  const effDef = ignoreDef ? 0 : defender.def;
  const base100 = Math.max(d.minDamage * 100, attacker.atk * d.atkScale - effDef * d.defScale);

  let percent = 100;
  const comboMul = attacker.skill === 'ink_combo' ? sp.inkComboMultiplier : 1;
  // Glue Guard: the attacker's streak gives no bonus against this defender.
  const steps = defender.skill === 'glue_guard' ? 0 : Math.max(0, inp.combo - 1);
  percent += Math.min(d.comboCapPercent * comboMul, steps * d.comboStepPercent * comboMul);

  const critChance = Math.min(d.critChanceCap, attacker.luck);
  const crit = inp.critRoll % 100 < critChance;
  if (crit) percent += attacker.skill === 'lucky_star' ? sp.luckyStarCritBonusPercent : d.critBonusPercent;

  if (attacker.skill === 'crayon_smash' && inp.attackerMove === 'rock') percent += sp.crayonSmashBonusPercent;

  const attackerHp = inp.attackerHp ?? attacker.maxHp;
  if (attacker.skill === 'marker_rage' && attackerHp > 0 && attackerHp * 2 < attacker.maxHp) percent += sp.markerRageBonusPercent;

  const st = RULES.stances;
  // Focus (winner): joins the percent sum like Crayon Smash.
  if (inp.attackerStance === 'focus') percent += st.focusBonusPercent;

  let damage = Math.max(d.minDamage, Math.floor((base100 * percent + 5000) / 10000));

  // Guard (winner): a careful hit deals less.
  if (inp.attackerStance === 'guard') damage = Math.max(d.minDamage, Math.floor((damage * (100 - st.guardPercent)) / 100));

  let shielded = false;
  if (defender.skill === 'paper_shield' && inp.defenderMove === 'paper') {
    shielded = true;
    damage = Math.max(d.minDamage, Math.floor((damage * (100 - sp.paperShieldReducePercent)) / 100));
  }

  // Loser's stance, after every other defender modifier.
  if (inp.defenderStance === 'guard') damage = Math.max(d.minDamage, Math.floor((damage * (100 - st.guardPercent)) / 100));
  else if (inp.defenderStance === 'focus') damage = Math.max(d.minDamage, Math.floor((damage * (100 + st.focusVulnerablePercent)) / 100));

  let dodged = false;
  if (
    defender.skill === 'doodle_dodge' &&
    inp.defenderMove !== 'timeout' &&
    inp.dodgeRoll % 100 < sp.doodleDodgeChance
  ) {
    dodged = true;
    damage = 0;
  }

  return { damage, crit, dodged, shielded };
}

/**
 * Resolve one simultaneous round. Returns the next state and the round record.
 * Stances are the *requested* ones; a stance sent while cooling down or with a
 * timed-out move is applied as 'none'.
 */
export function resolveRound(
  state: BattleState,
  moveA: ResolvedMove,
  moveB: ResolvedMove,
  stanceA: Stance = 'none',
  stanceB: Stance = 'none',
): { state: BattleState; result: RoundResultView } {
  if (state.winner !== null) throw new Error('battle already finished');

  // Exactly two rolls per round, always, so the stream never desyncs.
  const [critRoll, s1] = nextU32(state.rng);
  const [dodgeRoll, s2] = nextU32(s1);

  const hp: [number, number] = [state.hp[0], state.hp[1]];
  const combo: [number, number] = [state.combo[0], state.combo[1]];
  const healed: [number, number] = [0, 0];
  const moves: [ResolvedMove, ResolvedMove] = [moveA, moveB];
  const cd = state.stanceCooldown;
  const stances: [Stance, Stance] = [
    effectiveStance(stanceA, cd[0], moveA, state.ink[0]),
    effectiveStance(stanceB, cd[1], moveB, state.ink[1]),
  ];
  const stanceCooldown: [number, number] = [nextStanceCooldown(cd[0], stances[0]), nextStanceCooldown(cd[1], stances[1])];
  const winnerSeat = judge(moveA, moveB);

  let damage = 0;
  let crit = false;
  let dodged = false;
  let shielded = false;
  let recoil = 0;
  const ink: [number, number] = [state.ink[0], state.ink[1]];
  const chain: [Move[], Move[]] = [[...state.chain[0]], [...state.chain[1]]];
  const ult: [number, number] = [0, 0];
  const shield: [boolean, boolean] = [false, false];
  let burst: 0 | 1 | null = null;
  let burstDamage = 0;

  // Ultimates land first, both at once, judged on the HP the round started with.
  const startHp = state.hp;
  for (const seat of [0, 1] as const) {
    if (stances[seat] !== 'ultimate') continue;
    const me = state.fighters[seat];
    const foe = seat === 0 ? 1 : 0;
    const u = RULES.ultimates[me.skill] ?? RULES.ultimates.none;
    ink[seat] = 0;
    if (u.shield) shield[seat] = true;
    let percent = u.damagePercent ?? 0;
    if (u.rageBonusPercent && startHp[seat] * 2 < me.maxHp) percent += u.rageBonusPercent;
    if (percent > 0) {
      ult[seat] = Math.min(hp[foe], percentOf(state.fighters[foe].maxHp, percent));
      hp[foe] -= ult[seat];
    }
    const heal = u.drain ? ult[seat] : u.healPercent ? percentOf(me.maxHp, u.healPercent) : 0;
    if (heal > 0 && hp[seat] > 0) {
      const next = Math.min(me.maxHp, hp[seat] + heal);
      healed[seat] += next - hp[seat];
      hp[seat] = next;
    }
  }
  const ultKo = hp[0] === 0 || hp[1] === 0;

  if (ultKo) {
    // An ultimate knock-out ends the round before the hands are compared.
  } else if (winnerSeat === -1) {
    combo[0] = 0;
    combo[1] = 0;
    chain[0] = [];
    chain[1] = [];
    if (moveA !== 'timeout') {
      for (const seat of [0, 1] as const) {
        const f = state.fighters[seat];
        if (f.skill === 'eraser_mend' && hp[seat] < f.maxHp) {
          const heal = Math.max(1, Math.floor((f.maxHp * RULES.skillParams.eraserMendHealPercent) / 100));
          const next = Math.min(f.maxHp, hp[seat] + heal);
          healed[seat] += next - hp[seat];
          hp[seat] = next;
        }
      }
    }
  } else {
    const w = winnerSeat;
    const l = w === 0 ? 1 : 0;
    combo[w] += 1;
    combo[l] = 0;
    const out = computeDamage({
      attacker: state.fighters[w],
      defender: state.fighters[l],
      attackerMove: moves[w],
      defenderMove: moves[l],
      combo: combo[w],
      critRoll,
      dodgeRoll,
      attackerHp: hp[w],
      attackerStance: stances[w],
      defenderStance: stances[l],
    });
    ({ damage, crit, dodged, shielded } = out);
    if (shield[l]) {
      // The loser's ultimate shield blocks the whole hit.
      damage = 0;
      crit = false;
      dodged = false;
      shielded = true;
    }
    hp[l] = Math.max(0, hp[l] - damage);

    // After-hit skills, in this order: the winner drains, then the loser's spikes bite back.
    const att = state.fighters[w];
    const dfn = state.fighters[l];
    const sp = RULES.skillParams;
    if (damage > 0 && att.skill === 'highlighter_drain') {
      const heal = Math.min(att.maxHp - hp[w], Math.max(1, Math.floor((damage * sp.highlighterDrainPercent) / 100)));
      healed[w] += heal;
      hp[w] += heal;
    }
    if (damage > 0 && dfn.skill === 'sharpener_spikes' && hp[w] > 1) {
      // Spikes never knock the winner out: they stop at 1 HP.
      recoil = Math.min(hp[w] - 1, Math.max(1, Math.floor((damage * sp.sharpenerSpikesPercent) / 100)));
      hp[w] -= recoil;
    }

    ink[w] = Math.min(RULES.ink.max, ink[w] + RULES.ink.winGain);
    if (damage > 0) ink[l] = Math.min(RULES.ink.max, ink[l] + RULES.ink.hurtGain);

    chain[l] = [];
    const ext = extendChain(chain[w], moves[w] as Move);
    chain[w] = ext.chain;
    if (ext.burst && hp[l] > 0) {
      burst = w;
      burstDamage = Math.min(hp[l], percentOf(state.fighters[l].maxHp, RULES.chain.burstPercent));
      hp[l] -= burstDamage;
      ink[w] = Math.min(RULES.ink.max, ink[w] + RULES.ink.burstGain);
    }
  }

  const round = state.round + 1;
  let winner: BattleWinner = null;
  if (hp[0] === 0 && hp[1] === 0) {
    winner = 'draw';
  } else if (hp[0] === 0 || hp[1] === 0) {
    winner = hp[0] === 0 ? 1 : 0;
  } else if (round >= state.maxRounds) {
    // Compare remaining HP as a fraction of max HP without floats.
    const a = hp[0] * state.fighters[1].maxHp;
    const b = hp[1] * state.fighters[0].maxHp;
    winner = a === b ? 'draw' : a > b ? 0 : 1;
  }

  const result: RoundResultView = {
    round,
    moves,
    winner: winnerSeat,
    damage,
    crit,
    dodged,
    shielded,
    healed,
    recoil,
    hp: [hp[0], hp[1]],
    combo: [combo[0], combo[1]],
    stances,
    ink: [ink[0], ink[1]],
    ult,
    burst,
    burstDamage,
    chain: [[...chain[0]], [...chain[1]]],
  };

  return {
    state: { ...state, hp, combo, round, rng: s2, winner, log: [...state.log, result], stanceCooldown, ink, chain },
    result,
  };
}
